import { useCallback, useEffect, useRef, useState } from 'react';

import { getListProvider, Provider } from '@/services/api/providers';

/**
 * Backs the `<SearchSelect>` Client picker in the Import modal.
 *
 * - Fires an unscoped first page when `open` flips true.
 * - Debounces typed queries at 300ms before refetching from page 1 with
 *   `?search=`.
 * - Lazy-loads subsequent pages via `loadMore()`, appending to the list.
 * - Keeps a `Map<id, name>` label cache so the dropdown can still render
 *   the selected client's name after a narrowing search drops it out of
 *   the visible result set.
 *
 * Mirrors `useSearchableOptions` (invoices) but typed for `Provider` and
 * paginated rather than fetch-all.
 */

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_MS = 300;

export interface UseProvidersDropdownResult {
  options: Provider[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  setSearch: (value: string) => void;
  loadMore: () => void;
  labelFor: (id: string | number) => string;
}

export function useProvidersDropdown(
  open: boolean,
  /** Optional business-unit filter, e.g. 'SCHEDULED_INSTANT'. */
  revenueStream?: string
): UseProvidersDropdownResult {
  const [options, setOptions] = useState<Provider[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const labelCache = useRef<Map<string, string>>(new Map());

  // Reset when the host (modal) closes so the next open starts fresh and
  // we don't show stale results.
  useEffect(() => {
    if (!open) {
      setSearch('');
      setOptions([]);
      setPage(1);
      setHasMore(false);
      setError(null);
      return;
    }
  }, [open]);

  // Page-1 fetch (initial + on search change). Debounced so we don't fire
  // on every keystroke. Empty search fires immediately.
  useEffect(() => {
    if (!open) return;
    const trimmed = search.trim();
    let cancelled = false;

    const fire = () => {
      setLoading(true);
      setError(null);
      getListProvider({
        page: '1',
        size: String(PAGE_SIZE),
        search: trimmed || undefined,
        revenue_stream: revenueStream,
      })
        .then((res) => {
          if (cancelled) return;
          const data = res.data ?? [];
          setOptions(data);
          setPage(1);
          setHasMore(res.pagination?.lastPage > 1);
          for (const p of data) {
            labelCache.current.set(String(p.id), p.name);
          }
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          setError(
            e instanceof Error ? e.message : 'Failed to load providers'
          );
          setOptions([]);
          setHasMore(false);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };

    let timer: ReturnType<typeof setTimeout> | undefined;
    if (trimmed) {
      timer = setTimeout(fire, SEARCH_DEBOUNCE_MS);
    } else {
      fire();
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [open, search, revenueStream]);

  const loadMore = useCallback(() => {
    if (!open || loading || loadingMore || !hasMore) return;
    const nextPage = page + 1;
    const trimmed = search.trim();
    setLoadingMore(true);
    getListProvider({
      page: String(nextPage),
      size: String(PAGE_SIZE),
      search: trimmed || undefined,
      revenue_stream: revenueStream,
    })
      .then((res) => {
        const data = res.data ?? [];
        setOptions((prev) => [...prev, ...data]);
        setPage(nextPage);
        setHasMore(nextPage < (res.pagination?.lastPage ?? 0));
        for (const p of data) {
          labelCache.current.set(String(p.id), p.name);
        }
      })
      .catch((e: unknown) => {
        setError(
          e instanceof Error ? e.message : 'Failed to load more providers'
        );
        setHasMore(false);
      })
      .finally(() => setLoadingMore(false));
  }, [open, loading, loadingMore, hasMore, page, search, revenueStream]);

  const labelFor = useCallback(
    (id: string | number) => labelCache.current.get(String(id)) ?? '',
    []
  );

  return {
    options,
    loading,
    loadingMore,
    hasMore,
    error,
    setSearch,
    loadMore,
    labelFor,
  };
}

export type { Provider };
